/**
 * SyncedPreferencesEngine — SUNUCU-SENKRON TERCİH katmanının canlı çekirdeği.
 *
 * referans-parite — İKİ kaynak-referans kaynağının 1-1 birleşimi (TEBHOS'ta merkezi
 * kaynak-referans-tarzı UserSettings store'u olmadığından blob yönetimi bu dosyada):
 *   1. `features/user/state/SyncedPreferencesEngine.ts` (kaynak-referans): protobuf
 *      byte-düzeyi alan yardımcıları — parse/replace/changed/copy/merge.
 *      Alan-alan (top-level field) bayt karşılaştırması sayesinde BİLİNMEYEN
 *      alanlar dahi korunur (eski istemci yeni alanı ezmez).
 *   2. `features/user/state/UserSettings.ts` (kaynak-referans) syncedPreferences kısmı:
 *      local/wire kopyaları (localStorage, kaynak-referans ile AYNI anahtarlar),
 *      dirty-alan takibi, in-flight/recent-ack koruması, mikro-görev flush,
 *      PATCH /users/@me/settings {synced_preferences}, 429 exponential backoff,
 *      sekmeler-arası senkron (AppStorage.subscribe external).
 *
 * AKIŞ:
 *   - READY → ingestSyncedPreferencesBlob(blob, userId) → mergeIncoming →
 *     bağlı store'lar (makeSyncedField reaction'ları) alt-mesajları uygular.
 *   - Store değişimi → makeSyncedField → setSubPreference(field, msg) →
 *     dirty + flush → PATCH → backend Scylla'ya yazar + diğer oturumlara
 *     `user:settings-update` (WS `USER_SETTINGS_UPDATE`) yayınlar →
 *     karşı cihazda yine ingest → merge → store'lar anlık güncellenir.
 *
 * GERİYE-UYUM / MİGRASYON ("yerel doluysa ve sunucu boşsa yerelden yükle-yaz"):
 *   Store'lar makePersistent (JSON) hidrasyonunu KORUR. Sunucu blob'unda alan
 *   YOKSA (undefined) makeSyncedField remote'u "yok" sayar ve local-gözlem
 *   reaction'ı store'un JSON'dan gelen durumunu setSubPreference ile sunucuya
 *   İTER — ilk senkronda localStorage değerleri otomatik taşınır (kaynak-referansın
 *   kendi davranışı da budur; ekstra migrasyon kodu gerekmez).
 *
 * KAYNAK-REFERANSTAN BİLİNÇLİ SAPMALAR (belgeli):
 *   - normalizeLegacyMessageGroupSpacingPreference taşınMADI: kaynak-referans-tarihsel
 *     blob migrasyonu; TEBHOS blob'ları sıfırdan üretiliyor, legacy biçim yok.
 *   - Hesap-değişimi: kaynak-referans AuthSession.userId reaction'ı yerine ingest()
 *     userId parametresiyle beslenir (READY'de kimlik zaten bilinir);
 *     userId değişirse handleAccountTransition() otomatik çağrılır — kalıcı
 *     anahtarlar temizlenir, farklı hesaba tercih SIZMAZ.
 */

import { fromBinary, toBinary } from "@bufbuild/protobuf";
import { makeAutoObservable, runInAction } from "mobx";
import {
  createEmptySyncedPreferences,
  decodeSyncedPreferencesLenient,
  encodeSyncedPreferences,
  type SyncedPreferences,
  SyncedPreferencesSchema,
} from "@tebhos/schema";
import { apiFetch } from "@/lib/api/client";
import AppStorage from "@app/features/platform/state/PersistentStorage";
import { Logger } from "@app/features/platform/utils/Logger";

const logger = new Logger("SyncedPreferencesEngine");

export type { SyncedPreferences } from "@tebhos/schema";
// UserSettings bu üçünü Engine üzerinden bekliyor (schema'daki codec'lerin re-export'u).
export {
	createEmptySyncedPreferences,
	decodeSyncedPreferencesLenient,
	encodeSyncedPreferences,
} from "@tebhos/schema";

/** Kök mesajın gerçek alanları (protobuf-es meta alanları hariç). */
export type SyncedPreferencesField = Exclude<keyof SyncedPreferences, "$typeName" | "$unknown">;
export type SyncedPreferencesSubField = Exclude<SyncedPreferencesField, "sanitizeUrls">;

// ─── 1. BYTE-DÜZEYİ ALAN YARDIMCILARI (kaynak-referans SyncedPreferencesEngine.ts 1-1) ──

interface TopLevelFieldChunk {
  field: number;
  bytes: Uint8Array;
}

export const SYNCED_PREFERENCES_FIELDS = SyncedPreferencesSchema.fields.map(
  (field) => field.localName as SyncedPreferencesField,
);
export const SYNCED_PREFERENCES_FIELD_NAMES = new Set<string>(SYNCED_PREFERENCES_FIELDS.map(String));
const FIELD_TO_NUMBER = new Map<SyncedPreferencesField, number>(
  SyncedPreferencesSchema.fields.map((field) => [field.localName as SyncedPreferencesField, field.number]),
);
const NUMBER_TO_FIELD = new Map<number, SyncedPreferencesField>(
  SyncedPreferencesSchema.fields.map((field) => [field.number, field.localName as SyncedPreferencesField]),
);

export function isSyncedPreferencesField(value: unknown): value is SyncedPreferencesField {
  return typeof value === "string" && SYNCED_PREFERENCES_FIELD_NAMES.has(value);
}

function fieldNumber(field: SyncedPreferencesField): number {
  const number = FIELD_TO_NUMBER.get(field);
  if (number == null) {
    throw new Error(`Unknown synced preferences field: ${String(field)}`);
  }
  return number;
}

function fieldName(number: number): SyncedPreferencesField | null {
  return NUMBER_TO_FIELD.get(number) ?? null;
}

function toFieldNames(fieldNumbers: ArrayLike<number>): Array<SyncedPreferencesField> {
  const fields: Array<SyncedPreferencesField> = [];
  for (let index = 0; index < fieldNumbers.length; index += 1) {
    const number = fieldNumbers[index];
    const field = fieldName(number);
    if (field != null) fields.push(field);
  }
  return fields;
}

function parseTopLevelFieldChunks(bytes: Uint8Array): Array<TopLevelFieldChunk> {
  const chunks: Array<TopLevelFieldChunk> = [];
  let offset = 0;
  while (offset < bytes.byteLength) {
    const start = offset;
    const key = readVarint(bytes, offset);
    if (key == null) break;
    offset = key.nextOffset;
    const field = key.value >> 3;
    const nextOffset = skipWireValue(bytes, offset, key.value & 7);
    if (nextOffset == null) break;
    offset = nextOffset;
    chunks.push({ field, bytes: bytes.slice(start, offset) });
  }
  return chunks;
}

function indexFieldChunks(chunks: ReadonlyArray<TopLevelFieldChunk>): Map<number, Array<Uint8Array>> {
  const fields = new Map<number, Array<Uint8Array>>();
  for (const chunk of chunks) {
    const entries = fields.get(chunk.field) ?? [];
    entries.push(chunk.bytes);
    fields.set(chunk.field, entries);
  }
  return fields;
}

function readVarint(bytes: Uint8Array, offset: number): { value: number; nextOffset: number } | null {
  let value = 0;
  let shift = 0;
  while (offset < bytes.byteLength) {
    const byte = bytes[offset++];
    value |= (byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) {
      return { value, nextOffset: offset };
    }
    shift += 7;
  }
  return null;
}

function skipWireValue(bytes: Uint8Array, offset: number, wireType: number): number | null {
  if (wireType === 0) {
    return readVarint(bytes, offset)?.nextOffset ?? null;
  }
  if (wireType === 1) {
    const nextOffset = offset + 8;
    return nextOffset <= bytes.byteLength ? nextOffset : null;
  }
  if (wireType === 2) {
    const length = readVarint(bytes, offset);
    if (length == null) return null;
    const nextOffset = length.nextOffset + length.value;
    return nextOffset <= bytes.byteLength ? nextOffset : null;
  }
  if (wireType === 5) {
    const nextOffset = offset + 4;
    return nextOffset <= bytes.byteLength ? nextOffset : null;
  }
  return null;
}

function concatChunks(chunks: ReadonlyArray<Uint8Array>): Uint8Array {
  let total = 0;
  for (const chunk of chunks) total += chunk.byteLength;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

function replaceField(target: Uint8Array, source: Uint8Array, field: number): Uint8Array {
  const targetFields = parseTopLevelFieldChunks(target);
  const sourceChunks = indexFieldChunks(parseTopLevelFieldChunks(source)).get(field) ?? [];
  const pieces: Array<Uint8Array> = [];
  let inserted = false;
  for (const chunk of targetFields) {
    if (chunk.field === field) {
      if (!inserted) {
        for (const sourceChunk of sourceChunks) pieces.push(sourceChunk);
        inserted = true;
      }
      continue;
    }
    pieces.push(chunk.bytes);
  }
  if (!inserted) {
    for (const sourceChunk of sourceChunks) pieces.push(sourceChunk);
  }
  return concatChunks(pieces);
}

function chunksEqual(left: Array<Uint8Array> | undefined, right: Array<Uint8Array> | undefined): boolean {
  if (left == null || right == null) return left == null && right == null;
  if (left.length !== right.length) return false;
  return left.every((chunk, index) => {
    const other = right[index];
    if (chunk.byteLength !== other.byteLength) return false;
    for (let i = 0; i < chunk.byteLength; i += 1) {
      if (chunk[i] !== other[i]) return false;
    }
    return true;
  });
}

function changedFieldNumbers(left: Uint8Array, right: Uint8Array): Array<number> {
  const leftFields = indexFieldChunks(parseTopLevelFieldChunks(left));
  const rightFields = indexFieldChunks(parseTopLevelFieldChunks(right));
  const fieldNumbers = new Set([...leftFields.keys(), ...rightFields.keys()]);
  return Array.from(fieldNumbers)
    .sort((a, b) => a - b)
    .filter((field) => !chunksEqual(leftFields.get(field), rightFields.get(field)));
}

export function preferencesToBytes(preferences: SyncedPreferences): Uint8Array {
  return toBinary(SyncedPreferencesSchema, preferences);
}

export function preferencesFromBytes(bytes: Uint8Array): SyncedPreferences {
  if (bytes.byteLength === 0) return createEmptySyncedPreferences();
  return fromBinary(SyncedPreferencesSchema, bytes);
}

export function changedSyncedPreferenceFields(
  left: SyncedPreferences,
  right: SyncedPreferences,
): Array<SyncedPreferencesField> {
  return toFieldNames(changedFieldNumbers(preferencesToBytes(left), preferencesToBytes(right)));
}

export function copySyncedPreferenceField(
  target: SyncedPreferences,
  source: SyncedPreferences,
  field: SyncedPreferencesField,
): SyncedPreferences {
  const bytes = replaceField(preferencesToBytes(target), preferencesToBytes(source), fieldNumber(field));
  return preferencesFromBytes(bytes);
}

/**
 * Gelen (sunucu) kökünü yerel kökle alan-alan birleştirir (kaynak-referans 1-1):
 *   - protected (dirty/in-flight) alanlar: yerel kazanır (henüz itilmedi).
 *   - recently-acked alanlar: sunucu ESKİ kopyayı yansıtıyorsa yerel kazanır
 *     (PATCH-yanıt yarışı — 60sn pencere).
 *   - flush uçuştayken gelen ve uçuştaki kopyadan FARKLI alanlar: dirty
 *     işaretlenir (yanıt sonrası yeniden itilir).
 */
export function mergeIncomingSyncedPreferences(args: {
  local: SyncedPreferences;
  wire: SyncedPreferences;
  incoming: SyncedPreferences;
  protectedFields: Iterable<SyncedPreferencesField>;
  recentlyAckedFields: Iterable<SyncedPreferencesField>;
  inFlight: SyncedPreferences | null;
  syncInFlight: boolean;
}): {
  merged: SyncedPreferences;
  wire: SyncedPreferences;
  dirtyFields: Array<SyncedPreferencesField>;
} {
  const localBytes = preferencesToBytes(args.local);
  const incomingBytes = preferencesToBytes(args.incoming);
  const inFlightBytes = args.inFlight == null ? null : preferencesToBytes(args.inFlight);
  const protectedSet = new Set<number>();
  for (const field of args.protectedFields) protectedSet.add(fieldNumber(field));
  const ackedSet = new Set<number>();
  for (const field of args.recentlyAckedFields) ackedSet.add(fieldNumber(field));
  const localFields = indexFieldChunks(parseTopLevelFieldChunks(localBytes));
  const incomingFields = indexFieldChunks(parseTopLevelFieldChunks(incomingBytes));
  const inFlightFields =
    inFlightBytes == null
      ? new Map<number, Array<Uint8Array>>()
      : indexFieldChunks(parseTopLevelFieldChunks(inFlightBytes));
  let merged = incomingBytes;
  let wire = incomingBytes;
  const dirtyFieldNumbers: Array<number> = [];
  const fieldNumbers = new Set([...localFields.keys(), ...incomingFields.keys(), ...inFlightFields.keys()]);
  for (const field of Array.from(fieldNumbers).sort((a, b) => a - b)) {
    if (protectedSet.has(field)) {
      merged = replaceField(merged, localBytes, field);
      continue;
    }
    if (ackedSet.has(field) && !chunksEqual(incomingFields.get(field), localFields.get(field))) {
      merged = replaceField(merged, localBytes, field);
      wire = replaceField(wire, localBytes, field);
      continue;
    }
    if (
      args.syncInFlight &&
      inFlightBytes != null &&
      !chunksEqual(incomingFields.get(field), inFlightFields.get(field))
    ) {
      dirtyFieldNumbers.push(field);
    }
  }
  return {
    merged: preferencesFromBytes(merged),
    wire: preferencesFromBytes(wire),
    dirtyFields: toFieldNames(dirtyFieldNumbers),
  };
}

// ─── 2. BLOB YÖNETİCİSİ (kaynak-referans UserSettings.ts syncedPreferences kısmı 1-1) ──

const USER_SETTINGS_ENDPOINT = "/users/@me/settings";
const SYNCED_PREFERENCES_DIRTY_FIELDS_KEY = "UserSettings:syncedPreferencesDirtyFields";
const SYNCED_PREFERENCES_LOCAL_KEY = "UserSettings:syncedPreferencesLocal";
const SYNCED_PREFERENCES_WIRE_KEY = "UserSettings:syncedPreferencesWire";
const SYNCED_PREFERENCES_RECENT_ACK_KEY = "UserSettings:syncedPreferencesRecentAck";
const SYNCED_PREFERENCES_OWNER_KEY = "UserSettings:syncedPreferencesOwner";
const RECENT_SYNCED_PREFERENCES_ACK_WINDOW_MS = 60_000;

function readDirtySyncedPreferenceFields(): Set<SyncedPreferencesField> {
  try {
    const raw = AppStorage.getJSON<unknown>(SYNCED_PREFERENCES_DIRTY_FIELDS_KEY, []);
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.filter(isSyncedPreferencesField));
  } catch (error) {
    logger.warn("Dirty synced-preference alanları okunamadı:", error);
    return new Set();
  }
}

function writeDirtySyncedPreferenceFields(fields: ReadonlySet<SyncedPreferencesField>): void {
  try {
    if (fields.size === 0) {
      AppStorage.removeItem(SYNCED_PREFERENCES_DIRTY_FIELDS_KEY);
      return;
    }
    AppStorage.setJSON(SYNCED_PREFERENCES_DIRTY_FIELDS_KEY, Array.from(fields).map(String).sort());
  } catch (error) {
    logger.warn("Dirty synced-preference alanları yazılamadı:", error);
  }
}

function readPersistedSyncedPreferences(key: string): SyncedPreferences {
  try {
    const raw = AppStorage.getItem(key);
    if (raw == null || raw === "") return createEmptySyncedPreferences();
    return decodeSyncedPreferencesLenient(raw);
  } catch (error) {
    logger.warn(`Kalıcı synced preferences okunamadı (${key}):`, error);
    return createEmptySyncedPreferences();
  }
}

function writePersistedSyncedPreferences(key: string, preferences: SyncedPreferences): void {
  try {
    const encoded = encodeSyncedPreferences(preferences);
    if (encoded === "") {
      AppStorage.removeItem(key);
    } else {
      AppStorage.setItem(key, encoded);
    }
  } catch (error) {
    logger.warn(`Kalıcı synced preferences yazılamadı (${key}):`, error);
  }
}

function readPersistedRecentAck(): Map<SyncedPreferencesField, number> {
  try {
    const raw = AppStorage.getJSON<unknown>(SYNCED_PREFERENCES_RECENT_ACK_KEY, {});
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return new Map();
    const now = Date.now();
    const map = new Map<SyncedPreferencesField, number>();
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === "number" && Number.isFinite(value) && value > now && isSyncedPreferencesField(key)) {
        map.set(key, value);
      }
    }
    return map;
  } catch (error) {
    logger.warn("Recent-ack penceresi okunamadı:", error);
    return new Map();
  }
}

function writePersistedRecentAck(entries: ReadonlyMap<SyncedPreferencesField, number>): void {
  try {
    if (entries.size === 0) {
      AppStorage.removeItem(SYNCED_PREFERENCES_RECENT_ACK_KEY);
      return;
    }
    const obj: Record<string, number> = {};
    for (const [key, value] of entries) obj[String(key)] = value;
    AppStorage.setJSON(SYNCED_PREFERENCES_RECENT_ACK_KEY, obj);
  } catch (error) {
    logger.warn("Recent-ack penceresi yazılamadı:", error);
  }
}

function cloneSyncedPreferences(preferences: SyncedPreferences): SyncedPreferences {
  return preferencesFromBytes(preferencesToBytes(preferences));
}

class SyncedPreferencesEngineState {
  /** Yerel tercih kökü (bu cihazın otoritesi; localStorage'a kalıcı). */
  syncedPreferences: SyncedPreferences = readPersistedSyncedPreferences(SYNCED_PREFERENCES_LOCAL_KEY);
  /** READY/settings-update ile sunucu kopyası en az bir kez geldi mi. */
  private hydrated = false;
  /** Son bilinen aktif kullanıcı (hesap-değişimi izolasyonu için). */
  currentUserId: string | null = readPersistedOwner();
  private wireSyncedPreferences: SyncedPreferences = readPersistedSyncedPreferences(SYNCED_PREFERENCES_WIRE_KEY);
  private syncFlushTimer: ReturnType<typeof setTimeout> | "microtask" | null = null;
  private syncFlushInFlight = false;
  private syncFlushPendingPromise: Promise<void> | null = null;
  private syncFlushPendingResolvers: Array<{
    resolve: () => void;
    reject: (error: unknown) => void;
  }> = [];
  private syncConsecutive429s = 0;
  private dirtySyncedPreferenceFields: Set<SyncedPreferencesField> = readDirtySyncedPreferenceFields();
  private inFlightSyncedPreferenceFields: Set<SyncedPreferencesField> = new Set();
  private inFlightSyncedPreferences: SyncedPreferences | null = null;
  private recentlyAckedSyncedPreferenceFields: Map<SyncedPreferencesField, number> = readPersistedRecentAck();
  private accountEpoch = 0;

  constructor() {
    makeAutoObservable<
      SyncedPreferencesEngineState,
      | "wireSyncedPreferences"
      | "syncFlushTimer"
      | "syncFlushInFlight"
      | "syncFlushPendingPromise"
      | "syncFlushPendingResolvers"
      | "syncConsecutive429s"
      | "dirtySyncedPreferenceFields"
      | "inFlightSyncedPreferenceFields"
      | "inFlightSyncedPreferences"
      | "recentlyAckedSyncedPreferenceFields"
      | "accountEpoch"
    >(
      this,
      {
        wireSyncedPreferences: false,
        syncFlushTimer: false,
        syncFlushInFlight: false,
        syncFlushPendingPromise: false,
        syncFlushPendingResolvers: false,
        syncConsecutive429s: false,
        dirtySyncedPreferenceFields: false,
        inFlightSyncedPreferenceFields: false,
        inFlightSyncedPreferences: false,
        recentlyAckedSyncedPreferenceFields: false,
        accountEpoch: false,
      },
      { autoBind: true },
    );
    this.installSyncedPreferencesTabSync();
  }

  /** kaynak-referans installSyncedPreferencesTabSync 1-1 — başka sekme local'i değiştirirse merge et. */
  private installSyncedPreferencesTabSync(): void {
    try {
      AppStorage.subscribe(
        () => {
          try {
            const incoming = readPersistedSyncedPreferences(SYNCED_PREFERENCES_LOCAL_KEY);
            runInAction(() => this.mergeIncomingSyncedPreferences(incoming));
          } catch (error) {
            logger.warn("Sekmeler-arası synced-preferences değişimi uygulanamadı:", error);
          }
        },
        { key: SYNCED_PREFERENCES_LOCAL_KEY, source: "external" },
      );
    } catch (error) {
      logger.warn("Sekmeler-arası synced-preferences aboneliği kurulamadı:", error);
    }
  }

  private persistLocalSyncedPreferences(): void {
    writePersistedSyncedPreferences(SYNCED_PREFERENCES_LOCAL_KEY, this.syncedPreferences);
  }

  private persistWireSyncedPreferences(): void {
    writePersistedSyncedPreferences(SYNCED_PREFERENCES_WIRE_KEY, this.wireSyncedPreferences);
  }

  isHydrated(): boolean {
    return this.hydrated;
  }

  /** kaynak-referans UserSettings.getSubPreference 1-1 (makeSyncedField'in remote-kaynağı). */
  getSubPreference<F extends SyncedPreferencesSubField>(field: F): SyncedPreferences[F] | undefined {
    const value = this.syncedPreferences[field];
    return value === undefined ? undefined : value;
  }

  /** kaynak-referans UserSettings.setSubPreference 1-1 (makeSyncedField'in push-hedefi). */
  async setSubPreference<F extends SyncedPreferencesSubField>(
    field: F,
    value: NonNullable<SyncedPreferences[F]>,
  ): Promise<void> {
    runInAction(() => {
      this.markSyncedPreferenceFieldDirty(field);
      (this.syncedPreferences as SyncedPreferences)[field] = value;
    });
    this.persistLocalSyncedPreferences();
    return this.scheduleSyncedPreferencesFlush();
  }

  getSanitizeUrls(): boolean {
    return this.syncedPreferences.sanitizeUrls;
  }

  /** Kök bool alan (TEBHOS'ta UI'sı yok; parite için mevcut). */
  async setSanitizeUrls(value: boolean): Promise<void> {
    runInAction(() => {
      this.markSyncedPreferenceFieldDirty("sanitizeUrls");
      this.syncedPreferences.sanitizeUrls = value;
    });
    this.persistLocalSyncedPreferences();
    return this.scheduleSyncedPreferencesFlush();
  }

  /**
   * Sunucu blob'unu içeri al (READY + user:settings-update yolu).
   * `encoded`: base64 blob, `null` (sunucuda temiz) veya undefined (alan yok — dokunma).
   * `userId`: payload'daki kimlik — değişirse önce hesap-geçişi temizliği yapılır.
   */
  ingest(encoded: string | null | undefined, userId?: string | null): void {
    if (userId != null && userId !== "") {
      if (this.currentUserId != null && this.currentUserId !== userId) {
        logger.warn("synced_preferences: hesap değişti — yerel senkron durumu sıfırlanıyor.");
        this.handleAccountTransition();
      }
      this.currentUserId = userId;
      writePersistedOwner(userId);
    }
    if (encoded === undefined) return;
    this.hydrated = true;
    this.mergeIncomingSyncedPreferences(decodeSyncedPreferencesLenient(encoded));
    // kaynak-referans updateUserSettings kuyruğu: hidrasyon sonrası bekleyen dirty
    // alanlar (ör. localStorage-migrasyon itişleri, offline birikimi) flush edilir.
    if (
      this.dirtySyncedPreferenceFields.size > 0 ||
      changedSyncedPreferenceFields(this.syncedPreferences, this.wireSyncedPreferences).length > 0
    ) {
      void this.scheduleSyncedPreferencesFlush();
    }
  }

  /** kaynak-referans UserSettings.handleAccountTransition 1-1 — hesap değişiminde tam sıfırlama. */
  handleAccountTransition(): void {
    this.accountEpoch += 1;
    this.hydrated = false;
    this.syncedPreferences = createEmptySyncedPreferences();
    this.wireSyncedPreferences = createEmptySyncedPreferences();
    this.dirtySyncedPreferenceFields.clear();
    writeDirtySyncedPreferenceFields(this.dirtySyncedPreferenceFields);
    this.inFlightSyncedPreferenceFields.clear();
    this.inFlightSyncedPreferences = null;
    this.recentlyAckedSyncedPreferenceFields.clear();
    writePersistedRecentAck(this.recentlyAckedSyncedPreferenceFields);
    AppStorage.removeItem(SYNCED_PREFERENCES_LOCAL_KEY);
    AppStorage.removeItem(SYNCED_PREFERENCES_WIRE_KEY);
    if (this.syncFlushTimer != null && this.syncFlushTimer !== "microtask") {
      clearTimeout(this.syncFlushTimer);
    }
    this.syncFlushTimer = null;
    this.syncConsecutive429s = 0;
    const resolvers = this.syncFlushPendingResolvers;
    this.syncFlushPendingResolvers = [];
    this.syncFlushPendingPromise = null;
    for (const resolver of resolvers) {
      resolver.resolve();
    }
  }

  private markSyncedPreferenceFieldDirty(field: SyncedPreferencesField): void {
    this.dirtySyncedPreferenceFields.add(field);
    writeDirtySyncedPreferenceFields(this.dirtySyncedPreferenceFields);
  }

  private clearSyncedPreferenceFieldsDirty(fields: Iterable<SyncedPreferencesField>): void {
    let changed = false;
    for (const field of fields) {
      changed = this.dirtySyncedPreferenceFields.delete(field) || changed;
    }
    if (changed) {
      writeDirtySyncedPreferenceFields(this.dirtySyncedPreferenceFields);
    }
  }

  private markSyncedPreferenceFieldsAcked(fields: Iterable<SyncedPreferencesField>): void {
    const expiresAt = Date.now() + RECENT_SYNCED_PREFERENCES_ACK_WINDOW_MS;
    let changed = false;
    for (const field of fields) {
      if (this.dirtySyncedPreferenceFields.has(field)) continue;
      this.recentlyAckedSyncedPreferenceFields.set(field, expiresAt);
      changed = true;
    }
    if (changed) {
      writePersistedRecentAck(this.recentlyAckedSyncedPreferenceFields);
    }
  }

  private isRecentlyAckedSyncedPreferenceField(field: SyncedPreferencesField): boolean {
    const expiresAt = this.recentlyAckedSyncedPreferenceFields.get(field);
    if (expiresAt === undefined) return false;
    if (expiresAt > Date.now()) return true;
    this.recentlyAckedSyncedPreferenceFields.delete(field);
    writePersistedRecentAck(this.recentlyAckedSyncedPreferenceFields);
    return false;
  }

  private mergeIncomingSyncedPreferences(incoming: SyncedPreferences): void {
    const protectedFields = new Set<SyncedPreferencesField>([
      ...this.dirtySyncedPreferenceFields,
      ...this.inFlightSyncedPreferenceFields,
    ]);
    const recentlyAckedFields = SYNCED_PREFERENCES_FIELDS.filter((field) =>
      this.isRecentlyAckedSyncedPreferenceField(field),
    );
    const {
      merged,
      wire: nextWire,
      dirtyFields,
    } = mergeIncomingSyncedPreferences({
      local: this.syncedPreferences,
      wire: this.wireSyncedPreferences,
      incoming,
      protectedFields,
      recentlyAckedFields,
      inFlight: this.inFlightSyncedPreferences,
      syncInFlight: this.syncFlushInFlight,
    });
    for (const field of dirtyFields) {
      this.markSyncedPreferenceFieldDirty(field);
    }
    const wireChanged = changedSyncedPreferenceFields(nextWire, this.wireSyncedPreferences).length > 0;
    const localChanged = changedSyncedPreferenceFields(merged, this.syncedPreferences).length > 0;
    if (!wireChanged && !localChanged) {
      return;
    }
    this.wireSyncedPreferences = nextWire;
    this.persistWireSyncedPreferences();
    if (localChanged) {
      this.syncedPreferences = merged;
      this.persistLocalSyncedPreferences();
    }
  }

  private decodeSyncedPreferencesFromPatchResponse(
    responseBody: unknown,
    fallback: SyncedPreferences,
  ): SyncedPreferences {
    if (responseBody == null || typeof responseBody !== "object") {
      return cloneSyncedPreferences(fallback);
    }
    const candidate =
      (responseBody as { synced_preferences?: unknown }).synced_preferences ??
      (responseBody as { syncedPreferences?: unknown }).syncedPreferences;
    if (typeof candidate === "string" || candidate === null) {
      // strictNullChecks:false altında `=== null` daraltması unknown'ı
      // daraltmaz — imza (string | null | undefined) ile örtüşen cast şart.
      return decodeSyncedPreferencesLenient(candidate as string | null);
    }
    return cloneSyncedPreferences(fallback);
  }

  private scheduleSyncedPreferencesFlush(): Promise<void> {
    if (this.syncFlushPendingPromise == null) {
      this.syncFlushPendingPromise = new Promise<void>((resolve, reject) => {
        this.syncFlushPendingResolvers.push({ resolve, reject });
      });
    }
    if (this.syncFlushTimer != null || this.syncFlushInFlight) return this.syncFlushPendingPromise;
    if (!this.hydrated) return this.syncFlushPendingPromise;
    this.syncFlushTimer = "microtask";
    queueMicrotask(() => {
      if (this.syncFlushTimer !== "microtask") return;
      this.syncFlushTimer = null;
      void this.runSyncedPreferencesFlush();
    });
    return this.syncFlushPendingPromise;
  }

  private async runSyncedPreferencesFlush(): Promise<void> {
    if (this.syncFlushInFlight) return;
    const resolvers = this.syncFlushPendingResolvers;
    this.syncFlushPendingResolvers = [];
    const pendingPromise = this.syncFlushPendingPromise;
    this.syncFlushPendingPromise = null;
    const completeAll = (error?: unknown): void => {
      if (error !== undefined) {
        for (const r of resolvers) r.reject(error);
      } else {
        for (const r of resolvers) r.resolve();
      }
    };
    const changedFields = changedSyncedPreferenceFields(this.syncedPreferences, this.wireSyncedPreferences);
    if (changedFields.length === 0) {
      this.clearSyncedPreferenceFieldsDirty(SYNCED_PREFERENCES_FIELDS);
      completeAll();
      return;
    }
    const changedFieldSet = new Set(changedFields);
    const dirtyChangedFields = Array.from(this.dirtySyncedPreferenceFields).filter((field) =>
      changedFieldSet.has(field),
    );
    const fieldsInRequest = new Set<SyncedPreferencesField>(
      dirtyChangedFields.length > 0 ? dirtyChangedFields : changedFields,
    );
    this.syncFlushInFlight = true;
    const epochAtFlush = this.accountEpoch;
    const snapshotAtFlush = cloneSyncedPreferences(this.syncedPreferences);
    this.inFlightSyncedPreferenceFields = new Set(fieldsInRequest);
    this.inFlightSyncedPreferences = cloneSyncedPreferences(snapshotAtFlush);
    const encoded = encodeSyncedPreferences(snapshotAtFlush);
    try {
      const response = await apiFetch<Record<string, unknown>>(USER_SETTINGS_ENDPOINT, {
        method: "PATCH",
        body: JSON.stringify({ synced_preferences: encoded === "" ? null : encoded }),
      });
      if (this.accountEpoch !== epochAtFlush) {
        this.syncFlushInFlight = false;
        completeAll();
        return;
      }
      runInAction(() => {
        this.syncFlushInFlight = false;
        const stillChangedFields = new Set(changedSyncedPreferenceFields(snapshotAtFlush, this.syncedPreferences));
        const ackedFields: Array<SyncedPreferencesField> = [];
        for (const field of fieldsInRequest) {
          if (!stillChangedFields.has(field)) {
            this.dirtySyncedPreferenceFields.delete(field);
            ackedFields.push(field);
          }
        }
        writeDirtySyncedPreferenceFields(this.dirtySyncedPreferenceFields);
        this.inFlightSyncedPreferenceFields.clear();
        this.inFlightSyncedPreferences = null;
        const confirmed = this.decodeSyncedPreferencesFromPatchResponse(response, snapshotAtFlush);
        this.mergeIncomingSyncedPreferences(confirmed);
        this.markSyncedPreferenceFieldsAcked(ackedFields);
      });
      this.syncConsecutive429s = 0;
      completeAll();
      if (
        this.dirtySyncedPreferenceFields.size > 0 ||
        changedSyncedPreferenceFields(this.syncedPreferences, this.wireSyncedPreferences).length > 0
      ) {
        void this.scheduleSyncedPreferencesFlush();
      }
    } catch (error) {
      this.syncFlushInFlight = false;
      this.inFlightSyncedPreferenceFields.clear();
      this.inFlightSyncedPreferences = null;
      if (this.accountEpoch !== epochAtFlush) {
        completeAll();
        return;
      }
      if (this.isRateLimitError(error)) {
        this.syncConsecutive429s += 1;
        const retryAfterMs = this.extractRetryAfterMs(error) ?? this.syncBackoffMs();
        logger.warn(
          `synced_preferences PATCH rate-limit yedi; ${Math.round(retryAfterMs / 1000)}sn sonra tekrar ` +
            `(deneme ${this.syncConsecutive429s})`,
        );
        if (this.syncFlushPendingPromise == null) {
          this.syncFlushPendingPromise = pendingPromise ?? new Promise<void>(() => undefined);
        }
        this.syncFlushPendingResolvers.unshift(...resolvers);
        this.syncFlushTimer = setTimeout(() => {
          this.syncFlushTimer = null;
          void this.runSyncedPreferencesFlush();
        }, retryAfterMs);
        return;
      }
      logger.error("Synced preferences kaydedilemedi:", error);
      completeAll(error);
    }
  }

  private syncBackoffMs(): number {
    const exp = Math.min(this.syncConsecutive429s, 6);
    return Math.min(60000, 1000 * 2 ** exp);
  }

  private isRateLimitError(error: unknown): boolean {
    if (error == null || typeof error !== "object") return false;
    const status = (error as { status?: unknown }).status;
    return status === 429;
  }

  private extractRetryAfterMs(error: unknown): number | null {
    if (error == null || typeof error !== "object") return null;
    const candidate =
      (error as { retryAfter?: unknown }).retryAfter ??
      (error as { body?: { retry_after?: unknown } }).body?.retry_after;
    if (typeof candidate !== "number" || !Number.isFinite(candidate) || candidate < 0) return null;
    return Math.min(60000, Math.max(250, candidate * 1000));
  }
}

function readPersistedOwner(): string | null {
  try {
    const raw = AppStorage.getItem(SYNCED_PREFERENCES_OWNER_KEY);
    return raw == null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

function writePersistedOwner(userId: string): void {
  try {
    AppStorage.setItem(SYNCED_PREFERENCES_OWNER_KEY, userId);
  } catch {
    /* depolama yok — bellek-içi devam */
  }
}

/** SyncedPreferences blob'unun tekil (singleton) canlı kaynağı. */
export const SyncedPreferencesEngine = new SyncedPreferencesEngineState();

/**
 * Backend'den gelen `synced_preferences` blob'unu engine'e verir.
 * ReadyHandler (READY.user_settings) + user:settings-update yolundan çağrılır.
 * `null` = sunucuda blob temiz (hidrasyon yine gerçekleşir — migrasyon itişi
 * için gerekli); `undefined` = payload'da alan yok (dokunma).
 */
export function ingestSyncedPreferencesBlob(
  encoded: string | null | undefined,
  userId?: string | null,
): void {
  SyncedPreferencesEngine.ingest(encoded, userId);
}
